import React, { useEffect, useState } from "react";
import { FileText, X } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import * as XLSX from "xlsx";
import { Button } from "./ButtonUI";
import { getThisMonthTransactions } from "../utils/dashboardData";

export default function ReportGenerator({ stats, allItems, allTransactions }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);

  useEffect(() => {
    setSelectedMonth(new Date().getMonth() + 1);
    setSelectedYear(new Date().getFullYear());
  }, [isOpen]);

  // === PDF Generation ===
  const handleGeneratePDF = async () => {
    setIsGeneratingPDF(true); // start loading
    const modal = document.querySelector("#report-modal");
    if (modal) modal.style.display = "none";

    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const monthName = new Date(selectedYear, selectedMonth - 1).toLocaleString(
      "default",
      { month: "long" }
    );

    try {
      let currentY = 15;

      // === Title ===
      doc.setFontSize(16);
      doc.text(
        `${monthName} ${selectedYear} - Inventory Report`,
        pageWidth / 2,
        currentY,
        {
          align: "center",
        }
      );
      currentY += 10;

      // === Generated Date ===
      doc.setFontSize(11);
      doc.text(
        `Generated on: ${new Date().toLocaleDateString()}`,
        14,
        currentY
      );
      currentY += 10;

      // === Screenshot the Chart ===
      const chartEl = document.querySelector("#dashboard-charts");
      if (chartEl) {
        const canvas = await html2canvas(chartEl, { scale: 2, useCORS: true });
        const imgData = canvas.toDataURL("image/png");
        const imgWidth = pageWidth - 20;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        doc.addImage(imgData, "PNG", 10, currentY, imgWidth, imgHeight);
        currentY += imgHeight + 12;
      }

      // ===== SECTION 1: KPI SUMMARY =====
      autoTable(doc, {
        startY: currentY,
        head: [["KPI Metric", "Value"]],
        body: [
          ["Total Items", stats.totalItems],
          ["Total Stock In", stats.totalStockIn],
          ["Total Stock Out", stats.totalStockOut],
          [
            "Transactions (This Month)",
            allTransactions.filter((t) => {
              const d = new Date(t.created_on);
              return (
                d.getMonth() + 1 === selectedMonth &&
                d.getFullYear() === selectedYear
              );
            }).length,
          ],
        ],
        styles: { fontSize: 10 },
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          halign: "center",
        },
        theme: "grid",
        margin: { top: 10 },
      });

      currentY = doc.lastAutoTable.finalY + 10;

      // ===== SECTION 2: EXPIRY SUMMARY =====
      const today = new Date();
      const expiredItems = allItems.filter(
        (i) => i.expiryDate && new Date(i.expiryDate) < today
      );

      const exp30 = allItems.filter((i) => {
        if (!i.expiryDate) return false;
        const diffDays =
          (new Date(i.expiryDate) - today) / (1000 * 60 * 60 * 24);
        return diffDays > 0 && diffDays <= 30;
      });

      const exp60 = allItems.filter((i) => {
        if (!i.expiryDate) return false;
        const diffDays =
          (new Date(i.expiryDate) - today) / (1000 * 60 * 60 * 24);
        return diffDays > 30 && diffDays <= 60;
      });

      const exp90 = allItems.filter((i) => {
        if (!i.expiryDate) return false;
        const diffDays =
          (new Date(i.expiryDate) - today) / (1000 * 60 * 60 * 24);
        return diffDays > 60 && diffDays <= 90;
      });

      const expiryTables = [
        { title: "Expired Items", color: [192, 57, 43], data: expiredItems },
        { title: "Expiring in 30 Days", color: [211, 84, 0], data: exp30 },
        { title: "Expiring in 60 Days", color: [243, 156, 18], data: exp60 },
        { title: "Expiring in 90 Days", color: [241, 196, 15], data: exp90 },
      ];

      for (const { title, color, data } of expiryTables) {
        if (data.length > 0) {
          autoTable(doc, {
            startY: currentY,
            head: [[title, "Quantity", "Expiry Date"]],
            body: data.map((item) => [
              item.name || "(Unnamed Item)",
              ((item.quantity ?? 0) + " " + (item.unit || "")).trim(),
              item.expiryDate
                ? new Date(item.expiryDate).toLocaleDateString()
                : "—",
            ]),
            headStyles: {
              fillColor: color,
              textColor: 255,
              halign: "center",
            },
            styles: {
              fontSize: 9,
              cellPadding: 3,
            },
            theme: "grid",
            margin: { left: 14, right: 14 },
          });
          currentY = doc.lastAutoTable.finalY + 10; // move below last table
        } else {
          // Show a short note if empty
          doc.setFontSize(10);
          doc.setTextColor(150);
          doc.text(`${title}: None`, 16, currentY + 5);
          currentY += 12;
        }
      }

      currentY = doc.lastAutoTable.finalY + 10;

      // ===== SECTION 3: STOCK HEALTH =====
      const lowStockItems = allItems.filter(
        (i) => typeof i.reorderLevel === "number" && i.quantity < i.reorderLevel
      );

      const healthyStockItems = allItems.filter(
        (i) =>
          typeof i.reorderLevel === "number" &&
          i.quantity >= i.reorderLevel &&
          i.quantity > 0
      );

      if (lowStockItems.length > 0) {
        autoTable(doc, {
          startY: currentY,
          head: [["Low Stock Items", "Quantity", "Reorder Level"]],
          body: lowStockItems.map((item) => [
            item.name || "(Unnamed Item)",
            ((item.quantity ?? 0) + " " + (item.unit || "")).trim(),
            item.reorderLevel ?? 0,
          ]),
          styles: {
            fontSize: 9,
            cellPadding: 3,
          },
          headStyles: {
            fillColor: [230, 126, 34], // Orange
            textColor: 255,
            halign: "center",
          },
          theme: "grid",
          margin: { left: 14, right: 14 },
        });
        currentY = doc.lastAutoTable.finalY + 10;
      } else {
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text("All items are healthy (no low stock).", 16, currentY + 5);
        currentY += 12;
      }

      // ✅ Add Healthy Stock Section
      if (healthyStockItems.length > 0) {
        autoTable(doc, {
          startY: currentY,
          head: [["Healthy Stock Items", "Quantity", "Reorder Level"]],
          body: healthyStockItems.map((item) => [
            item.name || "(Unnamed Item)",
            ((item.quantity ?? 0) + " " + (item.unit || "")).trim(),
            item.reorderLevel ?? 0,
          ]),
          styles: {
            fontSize: 9,
            cellPadding: 3,
          },
          headStyles: {
            fillColor: [39, 174, 96], // Green
            textColor: 255,
            halign: "center",
          },
          theme: "grid",
          margin: { left: 14, right: 14 },
        });
        currentY = doc.lastAutoTable.finalY + 10;
      } else {
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text("No healthy stock items found.", 16, currentY + 5);
        currentY += 12;
      }

      // ===== SECTION 4: MOST MOVED ITEMS =====
      // --- Compute Top 5 Most Moved Items ---
      const movementMap = {}; // { itemName: { stockIn, stockOut } }
      const thisMonthTransactions = getThisMonthTransactions(
        allTransactions,
        selectedYear,
        selectedMonth
      );

      // console.log(allTransactions);

      thisMonthTransactions.forEach((t) => {
        const itemName = t.item?.name || "Unknown";
        if (!movementMap[itemName]) {
          movementMap[itemName] = { name: itemName, stockIn: 0, stockOut: 0 };
        }
        if (t.type === "Stock in") movementMap[itemName].stockIn += t.quantity;
        if (t.type === "Stock out")
          movementMap[itemName].stockOut += t.quantity;
      });

      // Sort by total movement (in + out)
      const sortedTop = Object.values(movementMap)
        .sort((a, b) => b.stockIn + b.stockOut - (a.stockIn + a.stockOut))
        .slice(0, 5);

      const topMoved = sortedTop.map((i, idx) => [
        idx + 1,
        i.name,
        i.stockIn || 0,
        i.stockOut || 0,
        (i.stockIn || 0) + (i.stockOut || 0),
      ]);

      autoTable(doc, {
        startY: currentY,
        head: [
          ["#", "Top Moved Items", "Stock In", "Stock Out", "Total Movement"],
        ],
        body: topMoved,
        styles: { fontSize: 10 },
        headStyles: {
          fillColor: [52, 152, 219],
          textColor: 255,
          halign: "center",
        },
        theme: "grid",
      });

      currentY = doc.lastAutoTable.finalY + 10;

      // ===== SECTION 5: TRANSACTION LIST =====
      const monthNum = Number(selectedMonth);
      const yearNum = Number(selectedYear);
      const filteredTxns = allTransactions.filter((t) => {
        const d = new Date(t.created_on);
        return d.getMonth() + 1 === monthNum && d.getFullYear() === yearNum;
      });

      const txnTable = filteredTxns.map((t, idx) => [
        idx + 1,
        t.item?.name || "",
        t.type || "",
        t.quantity || "",
        t.reason || "",
        new Date(t.created_on).toLocaleString(),
        t.created_by?.name || "",
      ]);

      //   if (currentY + txnTable.length * 10 > 280) {
      //     doc.addPage();
      //     currentY = 15;
      //   }

      autoTable(doc, {
        startY: currentY,
        head: [
          [
            "#",
            "TRANSACTION LIST",
            "Type",
            "Qty",
            "Reason",
            "Date",
            "Created by",
          ],
        ],
        body: txnTable,
        styles: {
          fontSize: 9,
          cellPadding: 3,
        },
        headStyles: {
          fillColor: [155, 89, 182],
          textColor: 255,
          halign: "center",
        },
        // Adjusted widths to fill A4 (≈190 mm usable width)
        // columnStyles: {
        //   0: { cellWidth: 10 }, // #
        //   1: { cellWidth: 80 }, // Item
        //   2: { cellWidth: 25 }, // Type
        //   3: { cellWidth: 20 }, // Qty
        //   4: { cellWidth: 40 }, // Date
        // },
        tableWidth: "auto",
        theme: "grid",
      });

      // === SAVE ===
      doc.save(`Inventory_Report_${selectedYear}-${selectedMonth}.pdf`);
    } catch (err) {
      console.error("Error generating PDF:", err);
    } finally {
      if (modal) modal.style.display = "block";
      setIsOpen(false);
      setIsGeneratingPDF(false); // stop loading
    }
  };

  const handleGenerateExcel = () => {
    setIsGeneratingExcel(true);

    try {
      const wb = XLSX.utils.book_new();

      const monthName = new Date(
        selectedYear,
        selectedMonth - 1
      ).toLocaleString("default", { month: "long" });

      const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const date = new Date(dateStr);
        if (isNaN(date)) return dateStr;
        return date.toLocaleString("en-US", {
          month: "short",
          day: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
      };

      // 🧮 Helper for nested values
      const getNestedValue = (obj, path) =>
        path.split(".").reduce((acc, key) => (acc ? acc[key] : ""), obj);

      // 🏷️ Helper to format headers
      const formatHeader = (key) =>
        key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

      // 🧾 1️⃣ KPI Summary Sheet
      const monthlyTx = allTransactions.filter((t) => {
        const d = new Date(t.created_on);
        return (
          d.getMonth() + 1 === selectedMonth && d.getFullYear() === selectedYear
        );
      });

      const totalStockIn = allTransactions
        .filter((t) => t.type === "IN")
        .reduce((sum, t) => sum + Number(t.quantity || 0), 0);
      const totalStockOut = allTransactions
        .filter((t) => t.type === "OUT")
        .reduce((sum, t) => sum + Number(t.quantity || 0), 0);

      const summaryData = [
        { Metric: "Total Items", Value: allItems.length },
        { Metric: "Total Stock In", Value: totalStockIn },
        { Metric: "Total Stock Out", Value: totalStockOut },
        { Metric: "Transactions This Month", Value: monthlyTx.length },
      ];
      const wsSummary = XLSX.utils.json_to_sheet(summaryData);

      // 🧾 2️⃣ Expiry Report Sheet
      const now = new Date();
      const addDays = (d, n) => new Date(d.getTime() + n * 86400000);

      const validItems = allItems.filter((i) => i.expiryDate);
      const expiredItems = validItems.filter(
        (i) => new Date(i.expiryDate) < now
      );
      const exp30 = validItems.filter(
        (i) =>
          new Date(i.expiryDate) >= now &&
          new Date(i.expiryDate) <= addDays(now, 30)
      );
      const exp60 = validItems.filter(
        (i) =>
          new Date(i.expiryDate) > addDays(now, 30) &&
          new Date(i.expiryDate) <= addDays(now, 60)
      );
      const exp90 = validItems.filter(
        (i) =>
          new Date(i.expiryDate) > addDays(now, 60) &&
          new Date(i.expiryDate) <= addDays(now, 90)
      );

      const expiryData = [
        ...expiredItems.map((i) => ({
          Category: "Expired",
          Item: i.name,
          Expiry: formatDate(i.expiryDate),
        })),
        ...exp30.map((i) => ({
          Category: "Expiring in 30 Days",
          Item: i.name,
          Expiry: formatDate(i.expiryDate),
        })),
        ...exp60.map((i) => ({
          Category: "Expiring in 60 Days",
          Item: i.name,
          Expiry: formatDate(i.expiryDate),
        })),
        ...exp90.map((i) => ({
          Category: "Expiring in 90 Days",
          Item: i.name,
          Expiry: formatDate(i.expiryDate),
        })),
      ];
      const wsExpiry = XLSX.utils.json_to_sheet(expiryData);

      // 🧾 3️⃣ Stock Health Sheet
      const lowStock = allItems.filter((i) => i.quantity <= i.reorderLevel);
      const healthyStock = allItems.filter((i) => i.quantity > i.reorderLevel);

      const stockHealthData = [
        ...lowStock.map((i) => ({
          Status: "Low Stock",
          Item: i.name,
          Quantity: i.quantity,
          Reorder_Level: i.reorderLevel,
        })),
        ...healthyStock.map((i) => ({
          Status: "Healthy",
          Item: i.name,
          Quantity: i.quantity,
          Reorder_Level: i.reorderLevel,
        })),
      ];
      const wsStock = XLSX.utils.json_to_sheet(stockHealthData);

      // 🧾 4️⃣ Most Moved Items
      const movementMap = {};
      allTransactions.forEach((t) => {
        const name = getNestedValue(t, "item.name");
        if (!name) return;
        movementMap[name] = (movementMap[name] || 0) + Number(t.quantity || 0);
      });
      const topMoved = Object.entries(movementMap)
        .sort((a, b) => b[1] - a[1])
        .map(([name, qty]) => ({ Item: name, Total_Movement: qty }));
      const wsTopMoved = XLSX.utils.json_to_sheet(topMoved);

      // 🧾 5️⃣ All Transactions (Detailed)
      const txnColumns = [
        "item.name",
        "type",
        "quantity",
        "reason",
        "created_on",
        "created_by.name",
      ];

      const monthNum = Number(selectedMonth);
      const yearNum = Number(selectedYear);

      const txnData = allTransactions
        .filter((t) => {
          const d = new Date(t.created_on);
          return d.getMonth() + 1 === monthNum && d.getFullYear() === yearNum;
        })
        .map((t) => {
          const obj = {};
          txnColumns.forEach((key) => {
            let val = getNestedValue(t, key);
            if (key === "created_on") val = formatDate(val);
            obj[formatHeader(key)] = val;
          });
          return obj;
        });
      const wsTransactions = XLSX.utils.json_to_sheet(txnData);

      // 🎨 Common header style
      const headerStyle = {
        fill: { fgColor: { rgb: "DCE6F1" } },
        font: { bold: true, color: { rgb: "000000" } },
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "999999" } },
          bottom: { style: "thin", color: { rgb: "999999" } },
          left: { style: "thin", color: { rgb: "999999" } },
          right: { style: "thin", color: { rgb: "999999" } },
        },
      };

      const applyHeaderStyle = (ws) => {
        const range = XLSX.utils.decode_range(ws["!ref"]);
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cellRef = XLSX.utils.encode_cell({ r: 0, c: C });
          if (ws[cellRef]) ws[cellRef].s = headerStyle;
        }
      };

      [wsSummary, wsExpiry, wsStock, wsTopMoved, wsTransactions].forEach((ws) =>
        applyHeaderStyle(ws)
      );

      // 📏 Column widths
      const setColWidth = (ws) => {
        const range = XLSX.utils.decode_range(ws["!ref"]);
        const widths = [];
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const header = ws[XLSX.utils.encode_cell({ r: 0, c: C })]?.v || "";
          widths.push({ wch: Math.max(15, header.length + 10) });
        }
        ws["!cols"] = widths;
      };
      [wsSummary, wsExpiry, wsStock, wsTopMoved, wsTransactions].forEach((ws) =>
        setColWidth(ws)
      );

      // 📘 Append sheets
      XLSX.utils.book_append_sheet(wb, wsSummary, "KPI Summary");
      XLSX.utils.book_append_sheet(wb, wsExpiry, "Expiry Report");
      XLSX.utils.book_append_sheet(wb, wsStock, "Stock Health");
      XLSX.utils.book_append_sheet(wb, wsTopMoved, "Top Moved Items");
      XLSX.utils.book_append_sheet(wb, wsTransactions, "Transactions");

      // 💾 Save file
      XLSX.writeFile(wb, `Inventory_Report_${monthName}_${selectedYear}.xlsx`);
    } catch (err) {
      console.error("Error generating Excel:", err);
    } finally {
      setIsOpen(false);
      setIsGeneratingExcel(false);
    }
  };

  return (
    <>
      {/* Button to open modal */}
      <Button
        variant="outline"
        className="flex gap-2"
        onClick={() => setIsOpen(true)}
      >
        <FileText className="w-4 h-4" />
        Generate Report
      </Button>

      {/* Modal */}
      {isOpen && (
        <div
          id="report-modal"
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
        >
          <div className="bg-white rounded-xl p-6 w-[400px] space-y-4 relative">
            {/* Header */}
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-lg font-semibold text-gray-800">
                Generate Report
              </h2>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-500 hover:text-gray-700 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="space-y-3">
              <div className="flex gap-2">
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  className="border rounded px-2 py-1 w-1/2 text-sm"
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {new Date(0, i).toLocaleString("default", {
                        month: "long",
                      })}
                    </option>
                  ))}
                </select>

                <input
                  type="number"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="border rounded px-2 py-1 w-1/2 text-sm"
                />
              </div>
            </div>

            {/* Footer buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button onClick={handleGeneratePDF}>
                {isGeneratingPDF ? "Generating PDF..." : "PDF"}
              </Button>
              <Button variant="outline" onClick={handleGenerateExcel}>
                {isGeneratingExcel ? "Generating Excel..." : "Excel"}
              </Button>
              <Button variant="ghost" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
